/**
 * Locate the table cells in a full-HTML email that hold human copy.
 *
 *   findEditableCells(html) -> [{ id, start, end, innerHtml, text, chars }]
 *
 * Pure: no Mongo, no network, no clock.
 *
 * DESIGN: this replaces the header/content/footer region split, which
 * measurement disproved — scripts/emails/splitDryRun.js could split 0 of 88 real
 * documents, because all 78 active PS templates keep their copy inside <td>
 * cells and none has prose outside table chrome. Editing a cell's contents in
 * place sidesteps that entirely: the chrome is never in the editor's hands, so
 * there is no split, no reassembly and no render-hash gate to get right.
 *
 * `start`/`end` are offsets of the cell's INNER content, exclusive of the <td>
 * tags, and `innerHtml` is literally html.slice(start, end) — never a rebuilt
 * string. That is what makes replaceCellContent's identity property hold; see
 * its header.
 *
 * Like splitTemplate, this guesses at nothing. A cell it cannot read with
 * certainty is simply not returned: it stays visible in the preview and is
 * edited through the raw HTML editor. A document with zero editable cells is a
 * normal outcome, not a failure.
 */

const { scanOutsideComments, isTagBalanced } = require('./htmlScan');

// Matches an opening or closing cell tag: group 1 is '/' on a close, group 2 the
// tag name, group 3 '/' on an explicitly self-closed <td/>. Scanned through
// scanOutsideComments so a <td> written inside a comment — MSO fallback markup
// is full of them — is never mistaken for a real cell.
//
// The attribute run skips over quoted values rather than using the [^>]* this
// file's neighbours use, because these offsets decide where the splice lands. A
// <td style="width:1px" title="a > b"> would otherwise end at the > inside the
// attribute, and the cell's content would be reported as starting with the
// leftover ` b">` — copy the editor would then quite reasonably delete, tearing
// the tag in half. An unterminated quote matches nothing and the cell is simply
// not offered: the conservative outcome. The three alternatives are disjoint on
// their first character, so there is no backtracking to blow up on.
const CELL_TAG = '<(/?)(td|th)\\b(?:[^>"\']|"[^"]*"|\'[^\']*\')*?(/?)>';

// Cells shorter than this are chrome, not copy: the corpus is full of spacer
// cells holding a single &nbsp; or a 1x1 shim GIF, and offering those as
// "copy to edit" would bury the two or three real ones in noise.
const MIN_TEXT_CHARS = 3;

const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'",
};

/**
 * Visible text of a fragment, for the length test and for the cell list the
 * admin renders.
 *
 * Every entity this does not know is replaced by a space rather than by a
 * guessed character. That undercounts a cell written entirely in numeric
 * entities, which errs towards calling it a spacer — the conservative direction
 * here, since a missed cell is editable through the HTML editor while a spacer
 * offered as copy is just noise. &nbsp; collapsing to whitespace is why the
 * spacer rule needs no special case of its own.
 */
function visibleText(fragment) {
  return fragment
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(
      /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi,
      (whole, name) => NAMED_ENTITIES[name.toLowerCase()] || ' ',
    )
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Every cell span in the document, innermost-first per nesting level, as
 * { ordinal, start, end } over the INNER content.
 *
 * Depth is tracked with an explicit stack rather than a lazy
 * /<td[^>]*>(.*?)<\/td>/ match, because these emails nest tables — and therefore
 * cells — several deep, and a lazy match would end the outer cell at the first
 * inner </td>, handing the editor a fragment that starts inside one element and
 * ends inside another.
 *
 * Unbalanced cell markup is dropped, never repaired: a </td> with nothing open
 * is ignored, and a <td> that never closes yields no span. Old markup that
 * relies on a <td> being implicitly closed by the next one therefore produces
 * no editable cell, which is the intended refusal rather than a guess at where
 * the author meant it to end.
 */
function cellSpans(html) {
  const spans = [];
  const open = [];
  let ordinal = -1;

  scanOutsideComments(html, CELL_TAG).forEach((match) => {
    const isClose = match[1] === '/';
    const isSelfClosing = match[3] === '/';

    if (!isClose) {
      // Every occurrence of a cell tag consumes an ordinal, editable or not, so
      // that ids stay put when a cell is skipped by the rules below.
      ordinal += 1;
      // <td/> holds no content; it still owns its ordinal but opens no span.
      if (!isSelfClosing) open.push({ ordinal, start: match.index + match[0].length });
      return;
    }

    if (open.length === 0) return;
    const cell = open.pop();
    spans.push({ ordinal: cell.ordinal, start: cell.start, end: match.index });
  });

  // Innermost cells close first, so sort back into document order — that is the
  // order the admin lists the editors in, and the order Task 3's probe reports.
  return spans.sort((a, b) => a.start - b.start);
}

function findEditableCells(html) {
  if (!html || typeof html !== 'string') return [];

  return cellSpans(html).reduce((cells, span) => {
    const innerHtml = html.slice(span.start, span.end);

    // A cell that nests a table is layout, not copy. Handing it to an editor
    // would put the editor in charge of the email's structure — and Tiptap,
    // whose schema has no <table> node, would delete it on the first save.
    // A nested <td>/<th> without a <table> is broken markup and gets the same
    // treatment: the inner cell is offered on its own instead.
    if (/<(table|td|th)\b/i.test(innerHtml)) return cells;

    // An MSO conditional is a second, Outlook-only rendering of the same block.
    // Editing one branch and not the other silently desynchronises them, and the
    // desync only shows up in Outlook, where nobody previews.
    if (/<!--\[if/i.test(innerHtml)) return cells;

    // The content must stand on its own once handed to an editor and spliced
    // back. A fragment that only balances when glued to its neighbours means the
    // span is not really this cell's content — refuse rather than guess.
    // This also keeps findEditableCells and replaceCellContent in agreement:
    // every cell returned here can be written back with ok:true.
    if (!isTagBalanced(innerHtml)) return cells;

    const text = visibleText(innerHtml);
    if (text.length < MIN_TEXT_CHARS) return cells;

    cells.push({
      id: `cell-${span.ordinal}`,
      start: span.start,
      end: span.end,
      innerHtml,
      text,
      chars: text.length,
    });
    return cells;
  }, []);
}

/**
 * Reasons the replacement copy itself is refused. Each returns a message, or
 * null when the content is acceptable.
 *
 * Every one of these REFUSES rather than sanitising. Silently stripping a
 * <table> an editor pasted, or quietly closing a tag they left open, hands back
 * a document that is not what they wrote and does not say so; being told the
 * save was rejected is the kinder failure.
 */
function contentViolation(innerHtml) {
  // Same rule as findEditableCells applies to the way back in: a cell that
  // nests a table (or a bare cell) is layout, and an editor who pastes one has
  // taken charge of the email's structure without meaning to.
  if (/<(table|td|th)\b/i.test(innerHtml)) {
    return 'replacement content contains a <table> or table cell — a cell that nests a table is layout, not copy';
  }
  // An MSO conditional pasted into a cell is an Outlook-only second rendering
  // that nothing in the preview will ever show, so nobody would catch it going
  // wrong.
  if (/<!--\[if/i.test(innerHtml)) {
    return 'replacement content contains an MSO conditional comment';
  }
  // The splice is blind: it writes the bytes between two offsets and cannot
  // notice that the new content closes a tag it never opened, which would tear
  // the surrounding table apart with nothing to flag it.
  if (!isTagBalanced(innerHtml)) {
    return 'replacement content is not tag-balanced on its own';
  }
  return null;
}

/**
 * Write one cell's inner content back, leaving every other byte alone.
 *
 *   replaceCellContent(html, cellId, innerHtml) -> { ok, reason, html }
 *
 * This is the whole safety story of the cell-editing design, and it replaces the
 * region plan's render-hash gate with something stronger: everything outside the
 * edited cell is byte-identical BY CONSTRUCTION, because the only edit ever made
 * is a splice at the offsets findEditableCells reported for this very string.
 *
 * The splice is string concatenation, never String.replace. A `$&` or `$1` in an
 * editor's copy is ordinary text to them and a replacement pattern to
 * String.replace, which is exactly the bug that was found and fixed in
 * reassemble() earlier in this project.
 *
 * Identity: because cell.innerHtml is defined as html.slice(start, end) and
 * nothing rewrites it, passing a cell's own content back yields
 * html.slice(0, start) + html.slice(start, end) + html.slice(end), which is html
 * for every 0 <= start <= end <= html.length. On any refusal the input html is
 * returned untouched, so a caller that ignores `ok` still cannot corrupt a
 * document.
 */
function replaceCellContent(html, cellId, innerHtml) {
  if (!html || typeof html !== 'string') return { ok: false, reason: 'empty html', html: '' };

  const refuse = (reason) => ({ ok: false, reason, html });

  if (typeof innerHtml !== 'string') return refuse('replacement content must be a string');

  // Cells are recomputed from this exact string rather than trusted from the
  // client: offsets shift on every save, and a stale pair of them would splice
  // into the middle of whatever now sits there.
  const cell = findEditableCells(html).find((candidate) => candidate.id === cellId);
  if (!cell) {
    return refuse(`no editable cell "${cellId}" in this template — it may have moved since the cells were read`);
  }

  const violation = contentViolation(innerHtml);
  if (violation) return refuse(violation);

  return {
    ok: true,
    reason: '',
    html: html.slice(0, cell.start) + innerHtml + html.slice(cell.end),
  };
}

/**
 * What fraction of a document's visible text the editable cells account for,
 * as a number between 0 and 1.
 *
 *   cellCoverage(html) -> 0..1
 *
 * WHY THIS EXISTS: a short cell list does not mean a short email. 11 of the 86
 * PS templates write their copy as a direct child of <table> or <tr> rather
 * than inside a <td>, and six of those surface exactly one trivial cell. An
 * admin shown "1 editable cell" on a full-page email would reasonably conclude
 * the rest is chrome and stop looking. This number lets the client say instead:
 * these cells are only a sliver of this email, edit the HTML directly.
 *
 * Both halves are measured with the same `visibleText` the finder measures
 * cells with, so the two can never disagree about what counts as text: the
 * covered half is the sum of the cells' own `chars`, and the uncovered half is
 * what visibleText finds in the document once those exact ranges are cut out.
 * Measuring the remainder rather than the whole is what makes a fully-covered
 * document score exactly 1 — comparing the cells against the whole document
 * instead charges the fraction for every space between two adjacent cells,
 * which no editor could ever recover.
 *
 * No cell returned by findEditableCells can contain another (a cell nesting a
 * td/th is rejected), so the ranges never overlap and nothing is counted twice.
 *
 * The remainder drops <head>, <style> and <script> before measuring. Their
 * contents are not tags, so visibleText would count every media query and CSS
 * declaration as document copy — and these emails carry kilobytes of it, which
 * would push all 86 templates under any useful threshold and make the field say
 * nothing.
 *
 * A document with no visible text at all scores 0 rather than 1: there is
 * nothing for the cell editors to work on, so pointing the client at the HTML
 * editor is the right answer, not "fully covered".
 */
function cellCoverage(html) {
  if (!html || typeof html !== 'string') return 0;

  const cells = findEditableCells(html);
  const covered = cells.reduce((sum, cell) => sum + cell.chars, 0);

  // Cut the cells out by offset — they are in document order and cannot
  // overlap — leaving a space so two neighbours never fuse into one word.
  let cursor = 0;
  const remainder = cells
    .reduce((parts, cell) => {
      parts.push(html.slice(cursor, cell.start));
      cursor = cell.end;
      return parts;
    }, [])
    .concat(html.slice(cursor))
    .join(' ')
    .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ');

  const total = covered + visibleText(remainder).length;
  if (total === 0) return 0;

  return covered / total;
}

module.exports = {
  // `cellSpans` is exported for ONE caller: the HTML-to-blocks converter
  // (ps-admin-template-store/src/utils/emailHtmlToBlocks.js). A converter has to
  // see the cells `findEditableCells` deliberately withholds — the ones holding
  // only an <img>, or only a styled rule — because those are content it must
  // place, not chrome it may ignore. The alternative was a second document
  // walker, which is exactly what the conversion probe warned against. Nothing
  // else should reach for this: a caller that wants editable copy wants
  // findEditableCells, which is this plus the rules that make a cell safe to
  // hand to an editor.
  cellSpans,
  findEditableCells,
  replaceCellContent,
  cellCoverage,
  visibleText,
};
